import { ChangeEvent, Fragment, useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '@upmc/ims.component-library';
import FormWrapper from '../../components/Form/FormWrapper';
import axiosInstance from '../../services/axios/axios';
import Edit from './containers/Edit';
import SubmissionSummary from './containers/SubmissionSummary';
import AccountDetailData from './components/AccountDetailData';
import FormEntry from './components/FormEntry';
import UARLayout from './containers/Layout';
import Icon from '../../components/UI/Icon/Icon';
import { TextboxTypes } from '../../components/UI/Input';
import { WrapImsPacket } from '../../store/utility';
import classes from './UserAccessReview.module.scss';
import * as actions from '../../store/uar/actions';
import * as utils from '../../utils';
import * as lifecycleWrapper from './util/lifecycleWrapper';
import {
	SUBMISSION_LOADING_BULK_MESSAGE,
	SUBMISSION_LOADING_TITLE,
	UAR_CONSTANTS,
} from './util/constants';
import { FORM_VALIDATION_SCHEMA as RENEW_VALIDATION_SCHEMA } from './UserAccessReviewRenew/_constants';

import Yup from 'utils/validation/yup-extensions';
import DatePicker from '../../components/Form/DatePicker/DatePicker';

/**
 * User Access Review Renew Component
 * @param {object} props
 */
const Renew = props => {
	/**
	 * Extract needed variables from the props provided by the `Scaffold`
	 */
	const {
		dispatch,
		loggedInUser,
		error,
		errorMessage,
		loading,
		setLoading,
		usersFound,
		bulkSelections,
		bulkFlow,
		esoFiId,
		canPerformActions,
		setCanPerformActions,
		loggedInEsoFiId,
	} = props;

	//STATE

	/**
	 * (set)Profile - if not set already, used to get and save user info from API
	 */
	const [profile, setProfile] = useState({});

	/**
	 * (set)Submitted - flag to indicate if submit button has been clicked
	 */
	const [submitted, setSubmitted] = useState(false);

	/**
	 * (set)SubmittedSuccessfully - flag to indicate if submission was successful
	 */
	const [submittedSuccessfully, setSubmittedSuccessfully] = useState(false);

	/**
	 * Gets the default renewal date (1 year from today)
	 * @returns formatted date string
	 */
	const getDefaultRenewalDate = () => {
		const today = new Date();
		const dd = String(today.getDate()).padStart(2, '0');
		const mm = String(today.getMonth() + 1).padStart(2, '0');
		const yyyy = today.getFullYear() + 1;
		return `${yyyy}-${mm}-${dd}`;
	};

	/**
	 * set(Form) - data for input form
	 */
	const [form, setForm] = useState({
		newRenewalDate: {
			label: 'Specify New Renewal Date',
			value: getDefaultRenewalDate(),
			options: [],
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: TextboxTypes.DATE,
			},
		},
	});
	//LIFECYCLE

	/**
	 * Renew Page - check permissions
	 */
	useEffect(() => {
		if (profile && Object.keys(profile).length > 0 && loggedInEsoFiId) {
			let valid = true;
			Object.keys(profile).forEach(key => {
				//assumption:  only three groups can perform actions:  supervisors, delegated owners, account coordinators
				const supervisor = profile[key].details?.supervisorEsoFiIds
					? profile[key].details?.supervisorEsoFiIds.includes(
							loggedInEsoFiId
					  )
					: false;
				const delegatedOwner =
					profile[key].details?.isDelegated &&
					profile[key].details?.delegatedOwnerEsoFiId
						? profile[key].details?.isDelegated &&
						  profile[key].details?.delegatedOwnerEsoFiId ===
								loggedInEsoFiId
						: false;
				const accountAdmin =
					profile[key].details?.accountAdminEsoFiId &&
					profile[key].details?.type === 'nonEmployee'
						? profile[key].details?.accountAdminEsoFiId ===
						  loggedInEsoFiId
						: false;
				valid = valid && (supervisor || delegatedOwner || accountAdmin);
			});
			setCanPerformActions(valid);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedInEsoFiId, profile, bulkSelections]);

	useEffect(() => {
		if (esoFiId) {
			setLoading(true);
			getAccountDetails(esoFiId)
				.then(result => {
					const newProfile = { ...profile };
					newProfile[esoFiId] = result[esoFiId];
					setProfile(newProfile);
					setLoading(false);
				})
				.catch(error => {
					setLoading(false);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [esoFiId]);

	useEffect(() => {
		if (bulkFlow && usersFound) {
			const newProfile = { ...profile };
			let promises = [];
			bulkSelections.forEach(selection => {
				if (!profile[selection.esoFiId]) {
					promises.push(getAccountDetails(selection.esoFiId));
				}
			});
			setLoading(true);
			Promise.all(promises).then(results => {
				results.forEach(selection => {
					Object.keys(selection).forEach(key => {
						if (!newProfile[key]) {
							newProfile[key] = selection[key];
						}
					});
				});
				setProfile(newProfile);
				setLoading(false);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bulkSelections]);

	const getAccountDetails = esoFiId => {
		return new Promise((resolve, reject) => {
			let targetProfile = null;
			let newDetails = { ...profile.details };
			let newApplications = { ...profile.applications };
			let newMobileDevices = { ...profile.mobileDevices };
			let newBadges = { ...profile.badges };
			let newServiceAccounts = { ...profile.serviceAccounts };

			let requestGetUserInfo = axiosInstance.get(
				'/api/UserAccessReview/GetUserInfo/' + esoFiId
			);
			let requestGetUserApps = axiosInstance.get(
				'/api/UserAccessReview/GetUserApps/' + esoFiId
			);
			let requestGetUserMobileDevices = axiosInstance.get(
				'/api/UserAccessReview/GetUserMobileDevices/' + esoFiId
			);
			let requestGetUserBadges = axiosInstance.get(
				'/api/UserAccessReview/GetUserBadges/' + esoFiId
			);
			let requestGetUserServiceAccounts = axiosInstance.get(
				'/api/UserAccessReview/GetUserServiceAccounts/' + esoFiId
			);
			axios
				.all([
					requestGetUserInfo,
					requestGetUserApps,
					requestGetUserMobileDevices,
					requestGetUserBadges,
					requestGetUserServiceAccounts,
				])
				.then(
					axios.spread((...responses) => {
						const responseGetUserInfo = responses[0];
						const responseGetUserApps = responses[1];
						const responseGetUserMobileDevices = responses[2];
						const responseGetUserBadges = responses[3];
						const responseGetUserServiceAccounts = responses[4];

						switch (responseGetUserInfo.status) {
							case 200:
								if (responseGetUserInfo.data.responseCode) {
									const responseCode =
										responseGetUserInfo.data.responseCode;
									let data = responseGetUserInfo.data.payload;
									switch (responseCode) {
										case 200:
											newDetails = data;
											break;
										default:
											break;
									}
								}
								break;

							default:
								break;
						}

						switch (responseGetUserApps.status) {
							case 200:
								if (responseGetUserApps.data.responseCode) {
									const responseCode =
										responseGetUserApps.data.responseCode;
									let data = responseGetUserApps.data.payload;
									switch (responseCode) {
										case 200:
											newApplications = data;
											break;
										default:
											break;
									}
								}
								break;

							default:
								break;
						}

						switch (responseGetUserMobileDevices.status) {
							case 200:
								if (
									responseGetUserMobileDevices.data
										.responseCode
								) {
									const responseCode =
										responseGetUserMobileDevices.data
											.responseCode;
									let data =
										responseGetUserMobileDevices.data
											.payload;
									switch (responseCode) {
										case 200:
											newMobileDevices = data;
											break;
										default:
											break;
									}
								}
								break;

							default:
								break;
						}

						switch (responseGetUserBadges.status) {
							case 200:
								if (responseGetUserBadges.data.responseCode) {
									const responseCode =
										responseGetUserBadges.data.responseCode;
									let data =
										responseGetUserBadges.data.payload;
									switch (responseCode) {
										case 200:
											newBadges = data;
											break;
										default:
											break;
									}
								}
								break;

							default:
								break;
						}

						switch (responseGetUserServiceAccounts.status) {
							case 200:
								if (
									responseGetUserServiceAccounts.data
										.responseCode
								) {
									const responseCode =
										responseGetUserServiceAccounts.data
											.responseCode;
									let data =
										responseGetUserServiceAccounts.data
											.payload;
									switch (responseCode) {
										case 200:
											newServiceAccounts = data;
											break;
										default:
											break;
									}
								}
								break;

							default:
								break;
						}

						targetProfile = {
							details: newDetails,
							applications: newApplications,
							mobileDevices: newMobileDevices,
							badges: newBadges,
							serviceAccounts: newServiceAccounts,
							expanded: false,
						};
					})
				)
				.then(() => {
					let newProfile = { ...profile };
					newProfile[esoFiId] = targetProfile;
					resolve(newProfile);
				})
				.catch(errors => {
					reject(errors);
				});
		});
	};

	//FUNCTIONS
	const removeHandler = member => {
		const newProfile = { ...profile };
		delete newProfile[member.details?.esoFiId];
		setProfile(newProfile);
		dispatch(
			actions.removeBulkSelection({
				payload: member.details,
			})
		);
	};

	const expandHandler = esoFiId => {
		let newProfile = { ...profile };
		if (newProfile[esoFiId]) {
			newProfile[esoFiId].expanded = !newProfile[esoFiId].expanded;
		}
		setProfile(newProfile);
	};

	/**
	 * actions to take upon form submission
	 */
	const submitHandler = () => {
		const isValid = validate();
		if (isValid) {
			let targets = [];
			if (bulkSelections && bulkSelections.length > 0) {
				targets = bulkSelections;
			} else {
				Object.keys(profile).forEach(key => {
					targets.push(profile[key].details);
				});
			}
			const payload = {
				loggedInUser: loggedInUser.loggedInUser,
				newRenewalDate: form.newRenewalDate.value,
				targets: targets,
			};

			setSubmitted(true);
			axiosInstance
				.post('/api/UserAccessReview/Renewal', WrapImsPacket(payload))
				.then(response => {
					switch (response.status) {
						case 200:
							if (response.data.responseCode) {
								const responseCode = response.data.responseCode;
								switch (responseCode) {
									case 200:
										setSubmittedSuccessfully(true);
										break;
									default:
										setSubmitted(false);
										setSubmittedSuccessfully(false);
										break;
								}
							}
							break;

						default:
							setSubmitted(false);
							setSubmittedSuccessfully(false);
							break;
					}
				})
				.catch(errors => {
					setSubmitted(false);
					setSubmittedSuccessfully(false);
				});
		}
		//TODO|BPK: else post message
	};

	/**
	 * event handler for form input changes
	 * @param {ChangeEvent<HTMLInputElement>} event
	 * @param {string} key
	 */
	const handleChange = (event, key) => {
		if (key in form) {
			let newForm = { ...form };
			newForm[key].value = event.target.value;
			setForm(newForm);
		}
	};

	/**
	 * form validation logic
	 * @param {ChangeEvent<HTMLInputElement>} event
	 */
	const validate = event => {
		const touchedKey =
			event && event.target && event.target.id ? event.target.id : null;
		let newForm = { ...form };
		let allValid = true;
		for (let key in newForm) {
			let valid = false;
			let value = newForm[key].value;
			let message = '';
			switch (key) {
				case 'newRenewalDate':
					if (newForm[key].visible) {
						const dateOneYearFuture = new Date();
						dateOneYearFuture.setFullYear(
							dateOneYearFuture.getFullYear() + 1
						);

						const isEmpty = value ? false : true;
						const isDate = utils.validateDateString(value);
						const isFuture =
							isDate && utils.isDateStringInFuture(value);
						const isOneYearFuture =
							isFuture &&
							!utils.isDateStringGreaterThan(
								value,
								dateOneYearFuture.toISOString().split('T')[0]
							);

						valid =
							!isEmpty && isDate && isFuture && isOneYearFuture;

						if (!isOneYearFuture) {
							message =
								'The date provided is more than one year in the future.';
						}
						if (!isFuture) {
							message =
								'The date provided is not in the future.  Please provide a future date.';
						}
						if (!isDate) {
							message = 'The value provided is not a valid date.';
						}
						if (isEmpty) {
							message = 'Please provide a valid future date.';
						}
					}
					break;
				default:
					break;
			}
			newForm[key].validation.valid = valid;
			newForm[key].validation.message = message;

			//no `touchedKey` indicates a submission event
			if (!touchedKey) {
				newForm[key].validation.touched = true;
			}

			allValid = allValid && valid;
		}

		//presence of `touchedKey` indicates an input focus-change event
		if (touchedKey) {
			newForm[touchedKey].validation.touched = true;
		}

		setForm(newForm);
		return allValid;
	};

	/**
	 * actions to take if cancelled
	 */
	const cancelHandler = () => {
		props.history.goBack();
	};

	//RENDER
	/**
	 * Retrieve settings from state and generate the input component
	 * @param {string} key - key of the `form` object
	 * @param {boolean} summary - (default=false) indicates whether this is intended for input or summary view
	 */
	const generateFormInput = (key, summary = false) => {
		let formInput = null;
		if (key in form && form[key].visible) {
			formInput = !summary ? (
				<FormEntry
					entryKey={key}
					formDetails={form[key]}
					handleChange={e => handleChange(e, key)}
					validateChange={validate}
					inputType={
						form[key].settings && form[key].settings.type
							? form[key].settings.type
							: 'text'
					}
					readOnly={
						(form[key].settings && form[key].settings.readOnly) ||
						submittedSuccessfully
					}
					placeholder={
						form[key].settings && form[key].settings.placeholder
							? form[key].settings.placeholder
							: null
					}
				/>
			) : (
				<FormEntry
					entryKey={key}
					formDetails={form[key]}
					inputType='SUMMARY'
				/>
			);
		}
		return formInput;
	};

	/**
	 * Form components
	 */
	const formOutput = (
		<div className={classes['list-container--scrollable']}>
			{profile
				? Object.keys(profile).map((key, index) => {
						const [
							details,
							applications,
							mobileDevices,
							badges,
							serviceAccounts,
							expanded,
						] = [
							profile[key].details,
							profile[key].applications,
							profile[key].mobileDevices,
							profile[key].badges,
							profile[key].serviceAccounts,
							profile[key].expanded,
						];
						const name =
							details?.firstName +
							' ' +
							(details?.middleInitial.length > 0
								? details?.middleInitial + ' '
								: '') +
							details?.lastName;

						return (
							<Fragment key={index}>
								<div
									className={classes['validate__container']}
									key={index}
								>
									<div
										className={
											classes['validate__container--data']
										}
									>
										<section>
											<span>Name:</span> {name}
										</section>
										<section>
											<span>Email:</span>{' '}
											{details?.email
												? details?.email
												: 'N/A'}
										</section>
										<section>
											<span>NTID:</span>{' '}
											{details?.ntid
												? details?.ntid
												: 'N/A'}
										</section>
										<section>
											<span>Employee Type:</span>{' '}
											{lifecycleWrapper.accountTypeLookup(
												details?.type
											)}
										</section>
										<section>
											<span>Business Unit:</span>{' '}
											{details?.businessUnit}
										</section>
										<section>
											<span>Cost Center:</span>{' '}
											{details?.costCenter}
										</section>
										<section>
											<span>Department:</span>{' '}
											{details?.departmentName}
										</section>
										<section>
											<span>Application Count:</span>{' '}
											{applications.length}
										</section>
										<section>
											<span>Mobile Devices Count:</span>{' '}
											{mobileDevices.length}
										</section>
										<section>
											<span>Badges Count:</span>{' '}
											{badges.length}
										</section>
										<section>
											<span>Service Account Count:</span>{' '}
											{serviceAccounts.length}
										</section>
										{details?.type ===
										UAR_CONSTANTS.NON_EMPLOYEE_KEY ? (
											<section>
												<span>
													Current Renewal Expiration
													Date:
												</span>{' '}
												{utils.prettyDateFormat(
													details?.dateValidationDue
												)}
											</section>
										) : null}
									</div>
									<div
										className={
											classes['validate__container--btn']
										}
									>
										<div
											className={
												classes[
													'validate__container--btn-vert'
												]
											}
										>
											<Button
												sx={{ m: '5px' }}
												disabled={false}
												onClick={() =>
													expandHandler(
														details?.esoFiId
													)
												}
												variant='outlined'
												endIcon={
													<Icon
														icon={
															expanded
																? 'unfold_less'
																: 'unfold_more'
														}
													/>
												}
											>
												{expanded
													? 'Less Info'
													: 'More Info'}
											</Button>
											<Link
												to={{
													pathname:
														'/UserAccessReview/AccountDetails',
													search:
														'?esoFiId=' +
														details?.esoFiId,
												}}
												className={utils.concatenateClassNames(
													['button__link--reset'],
													classes
												)}
											>
												<Button
													sx={{ m: '5px' }}
													disabled={false}
													endIcon={
														<Icon icon='edit' />
													}
												>
													Modify and Renew
												</Button>
											</Link>
											{bulkFlow ? (
												<Button
													sx={{ m: '5px' }}
													disabled={
														Object.keys(profile)
															.length <= 1
													}
													onClick={() =>
														removeHandler(
															profile[key]
														)
													}
													variant='outlined'
													endIcon={
														<Icon icon='remove_circle_outline' />
													}
												>
													Remove
												</Button>
											) : null}
										</div>
									</div>
								</div>
								{expanded ? (
									<AccountDetailData
										dispatch={dispatch}
										loggedInUser={loggedInUser}
										error={error}
										errorMessage={errorMessage}
										loading={loading}
										canPerformActions={false}
										profile={profile[key]}
										// Renew `profile` structure different than `AccountDetailData` `profile` structure
										setProfile={profileAccountDetailData => {
											let profileCopy = { ...profile };
											profileCopy[key] =
												profileAccountDetailData;
											setProfile(profileCopy);
										}}
										applications={applications}
										mobileDevices={mobileDevices}
										badges={badges}
										serviceAccounts={serviceAccounts}
										bulkSelections={bulkSelections}
										readOnly={true}
									/>
								) : null}
							</Fragment>
						);
				  })
				: null}
			<FormWrapper
				enableReinitialize
				initialValues={{
					newRenewalDate: form['newRenewalDate'].value,
				}}
				onSubmit={async (values, actions) => {
					actions.setSubmitting(false); // submission continued outside of Formik
				}}
				validationSchema={Yup.object().shape({
					...RENEW_VALIDATION_SCHEMA,
				})}
			>
				{({ formik }) => (
					<Fragment>
						<DatePicker
							type={'date'}
							name={'newRenewalDate'}
							error={formik.errors.newRenewalDate ? true : false}
							errorMessage={formik.errors.newRenewalDate}
							formDetails={form['newRenewalDate']}
							handleChange={e =>
								handleChange(e, 'newRenewalDate')
							}
							className={
								'UserAccessReview_edit-input__textbox__mdizd'
							}
							label={'* Specify New Renewal Date'}
						/>
					</Fragment>
				)}
			</FormWrapper>
		</div>
	);

	const formOutputSummary = (
		<div className={classes['list-container--scrollable']}>
			{profile
				? Object.keys(profile).map((key, index) => {
						const [details] = [profile[key].details];
						const name =
							details?.firstName +
							' ' +
							(details?.middleInitial.length > 0
								? details?.middleInitial + ' '
								: '') +
							details?.lastName;

						return (
							<div
								className={classes['validate__container']}
								key={index}
							>
								<div
									className={
										classes['validate__container--data']
									}
								>
									<section>
										<span>Name:</span> {name}
									</section>
									<section>
										<span>Email:</span>{' '}
										{details?.email
											? details?.email
											: 'N/A'}
									</section>
									<section>
										<span>NTID:</span>{' '}
										{details?.ntid ? details?.ntid : 'N/A'}
									</section>
									<section>
										<span>Employee Type:</span>{' '}
										{lifecycleWrapper.accountTypeLookup(
											details?.type
										)}
									</section>
									<section>
										<span>Business Unit:</span>{' '}
										{details?.businessUnit}
									</section>
									<section>
										<span>Cost Center:</span>{' '}
										{details?.costCenter}
									</section>
									<section>
										<span>Department:</span>{' '}
										{details?.departmentName}
									</section>
									<section>
										<span>Application Count:</span>{' '}
										{profile[key].applications &&
											profile[key].applications.length}
									</section>
									<section>
										<span>Mobile Devices Count:</span>{' '}
										{profile[key].mobileDevices &&
											profile[key].mobileDevices.length}
									</section>
									<section>
										<span>Badges Count:</span>{' '}
										{profile[key].badges &&
											profile[key].badges.length}
									</section>
									<section>
										<span>Service Account Count:</span>{' '}
										{profile[key].serviceAccounts &&
											profile[key].serviceAccounts.length}
									</section>
								</div>
							</div>
						);
				  })
				: null}

			{generateFormInput('newRenewalDate', true)}
		</div>
	);

	/**
	 * return JSX to render
	 */
	return (
		<UARLayout
			title='Renew Non-Employee Access'
			loading={loading}
			error={error}
			errorMessage={errorMessage}
			canPerformActions={canPerformActions}
		>
			{submittedSuccessfully ? (
				<SubmissionSummary
					profile={
						bulkSelections && bulkSelections.length > 0
							? null
							: profile[esoFiId].details
					}
				>
					<div>Validation Submitted for:</div>
					{formOutputSummary}
				</SubmissionSummary>
			) : (
				<Edit
					cancellable={{
						handler: cancelHandler,
						disabled: submitted,
					}}
					submittable={{
						handler: submitHandler,
						label:
							bulkSelections && bulkSelections.length > 0
								? 'Renew All'
								: 'Renew',
						disabled: submitted,
					}}
					loading={{
						isLoading: submitted,
						title: SUBMISSION_LOADING_TITLE,
						message: bulkFlow
							? SUBMISSION_LOADING_BULK_MESSAGE
							: (profile[esoFiId]?.serviceAccounts?.length ?? 0) >
							  50
							? 'The account you are renewing is associated with many service accounts which can take more time to process.'
							: null,
					}}
					profile={
						bulkSelections && bulkSelections.length > 0
							? null
							: profile &&
							  profile[esoFiId] &&
							  profile[esoFiId].details
							? profile[esoFiId].details
							: null
					}
				>
					{formOutput}
				</Edit>
			)}
		</UARLayout>
	);
};

export default Renew;
